import { apiFetch } from '@crowdex/host-kit/renderer/http';
import { Bell } from 'lucide-react';
import { useEffect, useState } from 'react';
import { SummarySection } from '@/components/desktop-summary/summary-primitives';
import { isDesktopPreviewPath } from '@/lib/assistants/directory';

/** A subscription of the conversation's agent, as the server lists it. */
export type ConversationSubscription = {
  id: string;
  types: string[];
  filter: Record<string, unknown>;
  reason: string | null;
  once: boolean;
  expiresAt: string | null;
};

/** `/desktop-preview…`: a task of a project waking on its pull request (D68). */
const PREVIEW_SUBSCRIPTIONS: ConversationSubscription[] = [
  {
    id: 'preview-pr',
    types: ['pull_request.*'],
    filter: { pullRequest: 556 },
    reason: 'PR #556 activity',
    once: false,
    expiresAt: null,
  },
];

/** While shown, the list follows what the agent subscribes to. */
const REFRESH_MS = 20_000;

/** "agent.replied · agent: Codex" — what a subscription waits for, in a line. */
export function subscriptionLabel(
  subscription: Pick<ConversationSubscription, 'types' | 'filter'>,
) {
  const types = subscription.types.map((type) => type.replace(/^crowdex\//, '')).join(', ');
  const filter = Object.entries(subscription.filter)
    .map(
      ([field, value]) => `${field}: ${Array.isArray(value) ? value.join(' | ') : String(value)}`,
    )
    .join(', ');
  return filter === '' ? types : `${types} · ${filter}`;
}

/** What the events of a known family mean to the user. */
const FAMILY_WORDS: readonly [RegExp, string][] = [
  [/^pull_request\./, 'CI results, reviews, comments'],
];

/** "CI results, reviews, comments": what a subscription waits for, in words when known. */
export function subscriptionDescription(
  subscription: Pick<ConversationSubscription, 'types' | 'filter'>,
): string {
  const types = subscription.types.map((type) => type.replace(/^crowdex\//, ''));
  const words = FAMILY_WORDS.find(([pattern]) => types.every((type) => pattern.test(type)));
  return words?.[1] ?? subscriptionLabel(subscription);
}

/**
 * The events the conversation's agent subscribed to, each waking it when it happens; the user
 * ends any of them here.
 */
export function SummarySubscriptionsSection({
  conversation,
  visible,
}: {
  conversation: string;
  visible: boolean;
}) {
  const [subscriptions, setSubscriptions] = useState<ConversationSubscription[]>([]);
  const query = `conversation=${encodeURIComponent(conversation)}`;

  useEffect(() => {
    // Another conversation's, or one hidden a while: nothing shown until it is read again.
    setSubscriptions([]);
    if (!visible) return;
    // The boards' (D68): a project's task wakes on its pull request's activity.
    if (isDesktopPreviewPath()) {
      setSubscriptions(PREVIEW_SUBSCRIPTIONS);
      return;
    }
    let cancelled = false;
    const load = () =>
      void apiFetch<ConversationSubscription[]>(`/api/events/subscriptions?${query}`, {
        cache: 'no-store',
      })
        .then((listed) => {
          if (!cancelled) setSubscriptions(listed);
        })
        // A conversation not saved yet, or offline: nothing to show.
        .catch(() => undefined);
    load();
    const timer = setInterval(load, REFRESH_MS);
    window.addEventListener('focus', load);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener('focus', load);
    };
  }, [query, visible]);

  if (subscriptions.length === 0) return null;
  const remove = (id: string) => {
    setSubscriptions((current) => current.filter((entry) => entry.id !== id));
    void apiFetch(`/api/events/subscriptions?${query}&id=${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }).catch(() => undefined);
  };
  return (
    <SummarySection card count={subscriptions.length} title="Wakes on">
      {subscriptions.map((subscription) => (
        <div className="flex min-w-0 items-center gap-2.5 py-1" key={subscription.id}>
          <Bell className="size-4 shrink-0 text-muted-foreground" />
          {/* What it is for first ("PR #556 activity"), then what it waits for (D68). */}
          <span className="min-w-0 flex-1">
            <span className="type-ui block truncate">
              {subscription.reason ?? subscriptionDescription(subscription)}
            </span>
            {subscription.reason && (
              <span className="type-caption line-clamp-2 text-muted-foreground">
                {subscriptionDescription(subscription)}
              </span>
            )}
          </span>
          <button
            aria-label="Stop waking on these events"
            className="type-meta h-7 shrink-0 rounded-control border border-hairline-strong px-3 text-foreground transition-colors hover:bg-accent/55"
            onClick={() => remove(subscription.id)}
            title="Stop waking on these events"
            type="button"
          >
            Stop waking
          </button>
        </div>
      ))}
    </SummarySection>
  );
}
